/**
 * The fee schedule editor's working copy. Identical in shape to
 * PartnerFeeSchedule, except that any number may be `undefined` while the
 * text typed into it is not a valid amount (so validation can say so rather
 * than silently dropping it), and a band says explicitly whether it is the
 * open-ended top band. `toSchedule` turns it back into the schema's shape;
 * PartnerFeeScheduleSchema is the only validator.
 */
import { PartnerFeeScheduleSchema, } from './schemas';
function ruleToDraft(rule) {
    return {
        floorCents: rule.floorCents,
        capCents: rule.capCents,
        components: rule.components.map((c) => {
            if (c.kind === 'banded') {
                return {
                    kind: 'banded',
                    base: c.base,
                    bands: c.bands.map((b) => ({ upToCents: b.upToCents ?? undefined, open: b.upToCents === null, amountCents: b.amountCents })),
                };
            }
            return { ...c };
        }),
    };
}
export function scheduleToDraft(s) {
    const payouts = {};
    for (const [stage, rule] of Object.entries(s.payouts)) {
        if (rule)
            payouts[stage] = ruleToDraft(rule);
    }
    return {
        template: s.template,
        gstRateBps: s.gstRateBps,
        agentFee: s.agentFee ? ruleToDraft(s.agentFee) : null,
        payouts,
        loading: { ...s.loading, propertyTypes: [...s.loading.propertyTypes] },
        defaultLvrBps: s.defaultLvrBps,
    };
}
function ruleFromDraft(r) {
    return {
        floorCents: r.floorCents,
        capCents: r.capCents,
        components: r.components.map((c) => c.kind === 'banded'
            ? { kind: 'banded', base: c.base, bands: c.bands.map((b) => ({ upToCents: b.open ? null : b.upToCents, amountCents: b.amountCents })) }
            : { ...c }),
    };
}
/** The draft in the schema's shape, with the given version. Not validated: pass it to `validateDraft`. */
export function toSchedule(d, version) {
    const payouts = {};
    for (const [stage, rule] of Object.entries(d.payouts)) {
        if (rule)
            payouts[stage] = ruleFromDraft(rule);
    }
    return {
        version,
        template: d.template,
        gstRateBps: d.gstRateBps,
        agentFee: d.agentFee ? ruleFromDraft(d.agentFee) : null,
        payouts,
        loading: d.loading,
        defaultLvrBps: d.defaultLvrBps,
    };
}
function friendly(issue) {
    // Typed text that did not parse (including more than two decimal places) reaches the schema as undefined.
    if (issue.code === 'invalid_type')
        return 'Enter a number with at most 2 decimal places';
    if (issue.code === 'too_big' && typeof issue.path[issue.path.length - 1] === 'string' && String(issue.path[issue.path.length - 1]).endsWith('Bps')) {
        return 'Must be 100% or less';
    }
    if (issue.code === 'too_big')
        return 'That amount is too large';
    if (issue.code === 'too_small')
        return 'Must be more than zero';
    return issue.message;
}
export function validateDraft(d, version) {
    const parsed = PartnerFeeScheduleSchema.safeParse(toSchedule(d, version));
    if (parsed.success)
        return { schedule: parsed.data, errors: {} };
    const errors = {};
    for (const issue of parsed.error.issues) {
        const key = issue.path.join('.');
        if (!errors[key])
            errors[key] = friendly(issue);
    }
    return { schedule: null, errors };
}
/** Errors at or under a path prefix (for a section summary). */
export function errorsUnder(errors, prefix) {
    return Object.entries(errors)
        .filter(([k]) => k === prefix || k.startsWith(`${prefix}.`))
        .map(([, v]) => v);
}
export function defaultRule() {
    return { components: [{ kind: 'fixed', amountCents: 0 }], floorCents: null, capCents: null };
}
